import type * as C from "@zooi/contracts";
import { reasonText } from "./labels.ts";
import { safeNext } from "./next-path.ts";
import { notifyFailure } from "./feedback.ts";
import { setWorkspaceScope, workspaceScope } from "./workspace-scope.ts";

// The SPA is one client of the public API: same routes, same contracts.

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly reason?: C.Reason;
  constructor(status: number, code: string, message: string, reason?: C.Reason) {
    super(message);
    this.status = status;
    this.code = code;
    this.reason = reason;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  try { return await requestData<T>(method, path, body); }
  catch (error) {
    const fieldError = error instanceof ApiError && error.reason?.code === "invalid_input" && error.reason.params?.field;
    if (method !== "GET" && !fieldError && error instanceof Error) notifyFailure(error);
    throw error;
  }
}

async function requestData<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  const scope = path.startsWith("/api/auth/") ? null : workspaceScope();
  try {
    res = await fetch(path, {
      method,
      credentials: "same-origin",
      headers: { ...(body === undefined ? {} : { "content-type": "application/json" }), ...(scope ? { "Zooi-Workspace": scope } : {}) },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, "network", reasonText({ code: "request_network" }));
  }
  let text: string;
  try { text = await res.text(); } catch { throw new ApiError(res.status, "network", reasonText({ code: "request_network" })); }
  let data;
  // A failure without a JSON body comes from the platform in front of the API
  // (for example a Workers resource-limit page), not from zooi.
  try { data = text ? JSON.parse(text) : null; } catch {
    const reason: C.Reason = { code: res.status >= 500 ? "service_unavailable" : "response_invalid" };
    throw new ApiError(res.status, "error", reasonText(reason), reason);
  }
  if (!res.ok) {
    // Only the bootstrap read may recover to another workspace. Never retry a write in a different scope.
    if (path === "/api/me" && scope && res.status === 403 && data?.error?.reason?.code === "workspace_not_found") {
      setWorkspaceScope(null);
      return requestData<T>(method, path, body);
    }
    const code = data?.error?.code ?? data?.code ?? "error";
    const rawReason = data?.error?.reason;
    const reason: C.Reason = rawReason && typeof rawReason.code === "string" && /^[a-z][a-z0-9_]*$/.test(rawReason.code)
      ? { code: rawReason.code, ...(rawReason.params && typeof rawReason.params === "object" && !Array.isArray(rawReason.params) ? {
          params: Object.fromEntries(Object.entries(rawReason.params).filter(([, value]) => value === null || typeof value === "string" || typeof value === "boolean" || typeof value === "number" && Number.isFinite(value))) as NonNullable<C.Reason["params"]>,
        } : {}) }
      : { code: "request_failed" };
    throw new ApiError(res.status, code, reasonText(reason), reason);
  }
  if (path === "/api/me" && data?.workspace?.id) setWorkspaceScope(data.workspace.id);
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body: unknown = {}) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
};

/** The OAuth consent page (plan M1, step 4): what an agent asks for, and the person's decision. */
export const oauthApi = {
  consent: (query: string) => api.get<C.ConsentView>(`/api/oauth/consent?oauth_query=${encodeURIComponent(query)}`),
  decide: (body: C.ConnectAgentInput) => api.post<{ redirect_to: string }>("/api/oauth/consent", body),
};

/** Query keys and fetchers, one place. */
export const q = {
  me: { queryKey: ["me"], queryFn: () => api.get<C.Me>("/api/me") },
  authConfig: { queryKey: ["auth-config"], queryFn: () => api.get<C.AuthConfigT>("/api/auth-config") },
  products: { queryKey: ["products"], queryFn: () => api.get<C.Product[]>("/api/products") },
  workspaceDashboard: (weeks?: 4 | 12) => ({ queryKey: ["workspace-dashboard", weeks ?? "default"], queryFn: () => api.get<C.WorkspaceDashboard>(`/api/workspace/dashboard${weeks ? `?weeks=${weeks}` : ""}`), refetchInterval: 60_000 }),
  workspaceModelKey: (workspaceId: string) => ({ queryKey: ["workspace-model-key", workspaceId], queryFn: () => api.get<C.WorkspaceModelKeyView>("/api/workspace/model-key") }),
  dispatchTarget: (productId: string) => ({ queryKey: ["dispatch-target", productId], queryFn: () => api.get<C.DispatchTargetView>(`/api/products/${encodeURIComponent(productId)}/dispatch`) }),
  personalTokens: (workspaceId: string, userId: string) => ({ queryKey: ["personal-tokens", workspaceId, userId], queryFn: () => api.get<C.PersonalToken[]>("/api/personal-tokens") }),
  product: (id: string) => ({ queryKey: ["product", id], queryFn: () => api.get<C.ProductDetail>(`/api/products/${id}`) }),
  growthMap: (id: string) => ({ queryKey: ["growth-map", id], queryFn: () => api.get<C.GrowthMap>(`/api/products/${id}/growth-map`) }),
  diagnosis: (id: string) => ({ queryKey: ["diagnosis", id], queryFn: () => api.get<C.Diagnosis | null>(`/api/products/${id}/diagnosis`) }),
  dataSetup: (id: string) => ({ queryKey: ["data-setup", id], queryFn: () => api.get<C.DataSetup>(`/api/products/${encodeURIComponent(id)}/data-setup`), refetchInterval: 30_000 }),
  ledger: (id: string) => ({ queryKey: ["ledger", id], queryFn: () => api.get<C.Ledger>(`/api/products/${encodeURIComponent(id)}/ledger`) }),
  sourceReview: (id: string) => ({ queryKey: ["source-review", id], queryFn: () => api.get<C.SourceReview>(`/api/products/${id}/source-review`) }),
  // Refetched every minute while the tab is visible, and on focus: the in-app notice (plan M1 step 5b).
  // Polls only while a first collection is under way.
  metricCollectors: (id: string) => ({ queryKey: ["metric-collectors", id], queryFn: () => api.get<C.MetricCollector[]>(`/api/products/${encodeURIComponent(id)}/metric-collectors`), refetchInterval: (query: { state: { data?: C.MetricCollector[] } }) => (query.state.data?.some((c) => !c.last_collected_at) ? 5_000 : false) }),
  metricLive: (id: string) => ({ queryKey: ["metric-live", id], queryFn: () => api.get<C.MetricLivePreview[]>(`/api/products/${encodeURIComponent(id)}/metrics/live`), staleTime: 60_000, refetchInterval: 60_000 }),
  eventIntake: (id: string) => ({ queryKey: ["event-intake", id], queryFn: () => api.get<C.EventIntake>(`/api/products/${encodeURIComponent(id)}/events`), refetchInterval: 60_000 }),
  ingestKeys: (id: string) => ({ queryKey: ["ingest-keys", id], queryFn: () => api.get<C.IngestKey[]>(`/api/products/${encodeURIComponent(id)}/ingest-keys`) }),
  runSettings: (id: string) => ({ queryKey: ["run-settings", id], queryFn: () => api.get<C.RunSettings>(`/api/products/${encodeURIComponent(id)}/run-settings`) }),
  agentConnections: (id: string) => ({ queryKey: ["agent-connections", id], queryFn: () => api.get<C.AgentConnection[]>(`/api/products/${encodeURIComponent(id)}/agent-connections`) }),
  week: (id: string, date?: string) => ({ queryKey: ["week", id, date ?? "current"], queryFn: () => api.get<C.WeekView>(`/api/products/${encodeURIComponent(id)}/week${date ? `?date=${encodeURIComponent(date)}` : ""}`) }),
  productRules: (id: string) => ({ queryKey: ["product-rules", id], queryFn: () => api.get<C.ProductRule[]>(`/api/products/${encodeURIComponent(id)}/rules`) }),
  todayBrief: (id: string) => ({ queryKey: ["today-brief", id], queryFn: () => api.get<C.TodayView>(`/api/products/${encodeURIComponent(id)}/brief`) }),
  grants: (id: string) => ({ queryKey: ["grants", id], queryFn: () => api.get<C.Grant[]>(`/api/products/${encodeURIComponent(id)}/grants`) }),
  gate: { queryKey: ["gate"], queryFn: () => api.get<C.GateReport>("/api/gate") },
  inbox: { queryKey: ["inbox"], queryFn: () => api.get<C.Inbox>("/api/inbox"), refetchInterval: 60_000 },
  productPlans: (id: string) => ({ queryKey: ["product-plans", id], queryFn: () => api.get<C.AgentPlan[]>(`/api/products/${encodeURIComponent(id)}/plans`) }),
  agentPlan: (id: string) => ({ queryKey: ["agent-plan", id], queryFn: () => api.get<C.AgentPlan>(`/api/plans/${encodeURIComponent(id)}`) }),
  workOrder: (id: string) => ({ queryKey: ["work-order", id], queryFn: () => api.get<C.ExecutionOrder>(`/api/work-orders/${encodeURIComponent(id)}`) }),
  postOrder: (id: string) => ({ queryKey: ["post-order", id], queryFn: () => api.get<C.PostOrder>(`/api/post-orders/${encodeURIComponent(id)}`) }),
  productCampaigns: (id: string) => ({ queryKey: ["product-campaigns", id], queryFn: () => api.get<C.Campaign[]>(`/api/products/${encodeURIComponent(id)}/campaigns`) }),
  campaignBoard: (id: string) => ({ queryKey: ["campaign-board", id], queryFn: () => api.get<C.CampaignBoard>(`/api/products/${encodeURIComponent(id)}/campaign-board`) }),
  campaign: (id: string) => ({ queryKey: ["campaign", id], queryFn: () => api.get<C.CampaignDetail>(`/api/campaigns/${encodeURIComponent(id)}`) }),
  campaignUnits: (id: string) => ({ queryKey: ["campaign-units", id], queryFn: () => api.get<C.CampaignUnits>(`/api/campaigns/${encodeURIComponent(id)}/units`) }),
  campaignReadingReview: (id: string) => ({ queryKey: ["campaign-reading-review", id], queryFn: () => api.get<C.CampaignReadingReview>(`/api/campaign-readings/${encodeURIComponent(id)}`) }),
  readingReview: (id: string) => ({ queryKey: ["reading-review", id], queryFn: () => api.get<C.ReadingReview>(`/api/readings/${encodeURIComponent(id)}`) }),
  replyQueue: (id: string) => ({ queryKey: ["reply-queue", id], queryFn: () => api.get<C.ReplyQueue>(`/api/reply-queues/${encodeURIComponent(id)}`) }),
  outreachOrder: (id: string) => ({ queryKey: ["outreach-order", id], queryFn: () => api.get<C.OutreachOrder>(`/api/outreach-orders/${encodeURIComponent(id)}`) }),
  today: (id: string) => ({ queryKey: ["today", id], queryFn: () => api.get<C.Today>(`/api/products/${id}/today`) }),
  plan: (id: string) => ({ queryKey: ["plan", id], queryFn: () => api.get<C.Plan>(`/api/products/${id}/plan`) }),
  goals: (id: string) => ({ queryKey: ["goals", id], queryFn: () => api.get<C.GoalTree>(`/api/products/${id}/goals`) }),
  supply: (id: string) => ({ queryKey: ["supply", id], queryFn: () => api.get<C.SupplyReport>(`/api/products/${id}/supply`) }),
  run: (id: string) => ({ queryKey: ["playbook-run", id], queryFn: () => api.get<C.PlaybookRun>(`/api/runs/${id}`) }),
  opportunities: (id: string, status?: string) => ({
    queryKey: ["opportunities", id, status ?? "all"],
    queryFn: () => api.get<C.OpportunityList>(`/api/products/${id}/opportunities${status ? `?status=${status}` : ""}`),
  }),
  actions: (id: string, status?: string) => ({
    queryKey: ["actions", id, status ?? "all"],
    queryFn: () => api.get<C.Action[]>(`/api/products/${id}/actions${status ? `?status=${status}` : ""}`),
    refetchInterval: 15_000,
  }),
  action: (id: string) => ({ queryKey: ["action", id], queryFn: () => api.get<C.ActionDetail>(`/api/actions/${id}`), refetchInterval: 15_000 }),
  actionActivity: (id: string) => ({ queryKey: ["action-activity", id], queryFn: () => api.get<C.ActionActivity>(`/api/actions/${encodeURIComponent(id)}/activity`), refetchInterval: 15_000 }),
  results: (id: string) => ({ queryKey: ["results", id], queryFn: () => api.get<C.Results>(`/api/products/${id}/results`) }),
  metrics: (id: string) => ({ queryKey: ["metrics", id], queryFn: () => api.get<C.MetricSummary[]>(`/api/products/${id}/metrics`) }),
  metricDashboard: (id: string, weeks: string, grain: string) => ({
    queryKey: ["metrics", id, "dashboard", weeks, grain],
    queryFn: () => api.get<C.MetricDashboard>(`/api/products/${id}/metrics/dashboard?weeks=${weeks}&grain=${grain}`),
  }),
  metricMeasurement: (id: string) => ({ queryKey: ["metric-measurement", id], queryFn: () => api.get<C.MetricMeasurement | null>(`/api/actions/${id}/metric-measurement`) }),
  actionPolicies: (id: string) => ({ queryKey: ["action-policies", id], queryFn: () => api.get<C.ActionPolicyBundle>(`/api/actions/${id}/policies`) }),
  workspacePolicies: (workspaceId: string) => ({ queryKey: ["workspace-policies", workspaceId], queryFn: () => api.get<C.WorkspacePolicy[]>("/api/workspace/policies") }),
  connections: (id: string) => ({ queryKey: ["connections", id], queryFn: () => api.get<C.Connection[]>(`/api/products/${id}/connections`) }),
  playbookLibrary: (productId: string) => ({ queryKey: ["playbook-library", productId], queryFn: () => api.get<C.PlaybookLibrary>(`/api/playbooks${productId ? `?product_id=${encodeURIComponent(productId)}` : ""}`) }),
  playbookPage: (id: string, productId: string) => ({ queryKey: ["playbook-page", id, productId], queryFn: () => api.get<C.PlaybookPage>(`/api/playbooks/${encodeURIComponent(id)}${productId ? `?product_id=${encodeURIComponent(productId)}` : ""}`) }),
  knowledge: { queryKey: ["knowledge"], queryFn: () => api.get<C.KnowledgeViewT>("/api/knowledge") },
  methods: (workspaceId: string, productId: string) => ({ queryKey: ["methods", workspaceId, productId], queryFn: () => api.get<C.MethodLibrary>(`/api/methods${productId ? `?product_id=${encodeURIComponent(productId)}` : ""}`) }),
};

/** Better Auth endpoints the sign-in page uses directly. */
/**
 * The signed authorization request an agent's OAuth flow put on this page's
 * address, if any. Sending it with the sign-in lets the provider resume the
 * authorization once the session exists.
 */
export function oauthQuery(search = window.location.search): string | null {
  const params = new URLSearchParams(search);
  params.delete("error");
  params.delete("error_description");
  return params.has("sig") && params.has("client_id") ? params.toString() : null;
}

/** Where the provider sends the browser next, when a sign-in resumed an authorization. */
const resumed = (response: unknown) => {
  const out = response as { redirect?: unknown; url?: unknown } | null;
  return out?.redirect === true && typeof out.url === "string" ? out.url : null;
};

export const authApi = {
  sendCode: (email: string) => api.post("/api/auth/email-otp/send-verification-otp", { email, type: "sign-in" }),
  verify: async (email: string, otp: string): Promise<string | null> => {
    const query = oauthQuery();
    const response = await api.post("/api/auth/sign-in/email-otp", { email, otp, ...(query ? { oauth_query: query } : {}) });
    setWorkspaceScope(null);
    return resumed(response);
  },
  social: (provider: "github" | "google") => {
    const query = oauthQuery();
    const params = new URLSearchParams(window.location.search);
    params.delete("error");
    params.delete("error_description");
    const callbackURL = safeNext(params.get("next"), window.location.origin) ?? "/";
    const errorCallbackURL = `/sign-in${params.size ? `?${params}` : ""}`;
    return api.post<{ url?: string }>("/api/auth/sign-in/social", { provider, callbackURL, errorCallbackURL, ...(query ? { oauth_query: query } : {}) }).then((r) => {
      if (!r.url) throw new ApiError(200, "response_invalid", reasonText({ code: "response_invalid" }));
      window.location.href = r.url;
    });
  },
  signOut: async () => { const response = await api.post("/api/auth/sign-out", {}); setWorkspaceScope(null); return response; },
  devMailbox: (email: string) => api.get<{ messages: { subject: string; at: string }[] }>(`/api/dev/mailbox?to=${encodeURIComponent(email)}`),
};
